/**
 * Opening a client relationship directly (Master Spec 6.2).
 *
 * The walk-in and the migrated relationship — somebody who was never a lead. A client
 * who enquired first should be created by CONVERTING their lead, which keeps the two
 * linked (BR-12); this screen is for the ones who did not.
 *
 * Only a name and a type are required. The reference, the status and the organization
 * are the service's to decide and are not offered here: the client is created
 * `prospective` and becomes active when they have an open matter.
 *
 * The first contact, if one is given, becomes the PRIMARY contact. That is the
 * service's behaviour rather than this screen's — `create_client` designates index 0 —
 * and it is worth knowing, because on a joint client the order decides who the firm
 * writes to.
 */

import {
  STAFF,
  qk,
  useApi,
  useApiMutation,
  type ClientDetail,
  type CustomFieldValues,
} from '@legalcrm/api';
import {
  Card,
  CardBody,
  Form,
  FormActions,
  FormInput,
  FormSelect,
  useZodForm,
} from '@legalcrm/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { CustomFieldInputs, problemsFrom } from '@/components/customFields/CustomFields';
import { PageHeader } from '@/shell/PageHeader';

import { RELATIONSHIP_OPTIONS } from './contactOptions';
import { useFirmMembers } from '../settings/useFirmMembers';

const schema = z.object({
  display_name: z.string().min(1, 'Enter the client’s name'),
  client_type: z.enum(['individual', 'organization']),
  owner_user: z.string(),
  responsible_lawyer: z.string(),
  contact_full_name: z.string(),
  contact_relationship: z.string(),
  contact_email: z.string().email('Enter a valid email address').or(z.literal('')),
  contact_phone: z.string(),
});

type Values = z.infer<typeof schema>;

interface NewContact {
  full_name: string;
  relationship: string;
  email?: string;
  phone?: string;
}

interface CreatePayload {
  display_name: string;
  client_type: string;
  owner_user?: string;
  responsible_lawyer?: string;
  contacts?: NewContact[];
  custom_fields?: CustomFieldValues;
}

export function NewClientPage() {
  const navigate = useNavigate();
  const api = useApi();
  const members = useFirmMembers({ assignableOnly: true });

  const peopleOptions = [
    { value: '', label: 'Nobody yet' },
    ...members.members.map((member) => ({
      // The `User` id, NOT the membership id: `owner_user` and `responsible_lawyer`
      // are foreign keys to `identity.User`.
      value: member.user,
      label: member.full_name || member.email,
    })),
  ];

  const form = useZodForm(schema, {
    defaultValues: {
      display_name: '',
      client_type: 'individual',
      owner_user: '',
      responsible_lawyer: '',
      contact_full_name: '',
      contact_relationship: 'self',
      contact_email: '',
      contact_phone: '',
    },
  });

  /**
   * Outside the zod schema on purpose — these fields are defined by the firm at
   * runtime. The server checks them against the same definitions the CSV importer
   * uses, so a value typed here and a value in a spreadsheet are held to one rule.
   */
  const [custom, setCustom] = useState<CustomFieldValues>({});

  const create = useApiMutation<ClientDetail, Values>({
    mutationFn: (values) => {
      const payload: CreatePayload = {
        display_name: values.display_name,
        client_type: values.client_type,
        // The API takes an ABSENT value, not an empty string, for anything optional.
        // "" would record that this client has no responsible lawyer rather than that
        // nobody has chosen one yet — and against a UUID field it is a 400 besides.
        owner_user: values.owner_user || undefined,
        responsible_lawyer: values.responsible_lawyer || undefined,
        custom_fields: custom,
      };

      // Only send a contact when one was actually named: `full_name` is required by
      // the contact serializer, so an empty block is a rejection rather than an
      // omission.
      const contactName = values.contact_full_name.trim();
      if (contactName) {
        payload.contacts = [
          {
            full_name: contactName,
            relationship: values.contact_relationship,
            email: values.contact_email || undefined,
            phone: values.contact_phone || undefined,
          },
        ];
      }

      return api.post<ClientDetail>(`${STAFF.clients}/`, payload);
    },
    invalidates: [qk.all('clients')],
  });

  return (
    <>
      <PageHeader
        title="New client"
        description="For a client who walked in. Somebody who enquired first should be converted from their lead instead, so the two stay linked."
      />

      <Card className="max-w-2xl">
        <CardBody>
          <Form
            form={form}
            ariaLabel="New client"
            error={create.error}
            onSubmit={async (values) => {
              const client = await create.mutateAsync(values);
              navigate(`/clients/${client.id}`);
            }}
          >
            <FormInput
              form={form}
              name="display_name"
              label="Name"
              hint="For an organisation, its registered name."
              autoFocus
              autoComplete="off"
            />

            <FormSelect
              form={form}
              name="client_type"
              label="Type"
              options={[
                { value: 'individual', label: 'Individual' },
                { value: 'organization', label: 'Organisation' },
              ]}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormSelect
                form={form}
                name="responsible_lawyer"
                label="Responsible lawyer"
                options={peopleOptions}
                hint={
                  members.canView
                    ? 'Can be set later.'
                    : 'The firm directory is not available to you. This can be set later.'
                }
              />
              <FormSelect
                form={form}
                name="owner_user"
                label="Owner"
                options={peopleOptions}
                hint="Defaults to you."
              />
            </div>

            <fieldset className="mt-2 flex flex-col gap-4 rounded border border-line p-4">
              <legend className="px-1 text-sm font-medium text-ink">First contact</legend>
              <p className="text-sm text-ink-muted">
                Optional. The first contact becomes the primary one. More can be added,
                and the primary changed, on the client’s record.
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput form={form} name="contact_full_name" label="Contact name" />
                <FormSelect
                  form={form}
                  name="contact_relationship"
                  label="Relationship to the client"
                  options={RELATIONSHIP_OPTIONS}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  form={form}
                  name="contact_email"
                  label="Email"
                  type="email"
                  hint="Needed before this contact can be invited to the portal."
                />
                <FormInput form={form} name="contact_phone" label="Telephone" type="tel" />
              </div>
            </fieldset>

            <CustomFieldInputs
              entity="client"
              values={custom}
              onChange={setCustom}
              problems={problemsFrom(create.error)}
            />

            <FormActions
              submitLabel="Create client"
              busy={create.isPending}
              onCancel={() => navigate('/clients')}
            />
          </Form>
        </CardBody>
      </Card>
    </>
  );
}
